import { NodeViewWrapper, type ReactNodeViewProps } from '@tiptap/react';
import React, { useEffect } from 'react';

import { Avatar, Tooltip, Text, Box } from '@vimeo/bokeh';
import { useIsBokeh } from '@vimeo/core/hooks/use-is-bokeh';

import { ANONYMOUS_USER_ICON } from './constants';

interface CardProps {
  label: string;
  avatarUri: string;
}

const UserHoverCard = ({ label, avatarUri }: CardProps) => {
  return (
    <Box display="flex" alignItems="center" gap="10px" px="6px">
      <Avatar alt="avatar" size="xs" src={avatarUri} nameProps={{ name: label }} />
      <Box
        fontSize="md"
        lineHeight="16px"
        overflow="hidden"
        whiteSpace="nowrap"
        textOverflow="ellipsis"
      >
        {label}
      </Box>
    </Box>
  );
};

const HighlightedText = ({ text, from, to }: { text: string; from?: number; to?: number }) => {
  if (text && from !== undefined && to !== undefined) {
    const pre = text.slice(0, from);
    const highlighted = text.slice(from, to);
    const post = text.slice(to);
    return (
      <>
        {pre}
        <mark className="highlighted-mention-text">{highlighted}</mark>
        {post}
      </>
    );
  }
  return <>{text}</>;
};

interface MentionAttrs {
  label: string;
  name?: string;
  teamUserId?: string;
  userId?: string;
  email?: string;
  avatar?: string;
  highlight?: {
    from: number;
    to: number;
  };
}

const MentionNode = ({
  node: { attrs: rawAttrs },
  extension: {
    options: { onMentionRemove, onMentionAdd },
  },
}: ReactNodeViewProps) => {
  const attrs = rawAttrs as unknown as MentionAttrs;
  useEffect(() => {
    onMentionAdd?.(attrs);

    return () => {
      if (onMentionRemove && (attrs.teamUserId || attrs.userId)) {
        onMentionRemove(
          attrs.userId ? parseInt(attrs.userId ?? '') : parseInt(attrs.teamUserId ?? ''),
        );
      }
    };
  }, [onMentionRemove, attrs.userId, attrs.teamUserId]);
  const isBokeh = useIsBokeh();
  const tooltipText = attrs.name ?? attrs.label; // needed for search and highlight tooltip
  const avatarUri = attrs.avatar ?? ANONYMOUS_USER_ICON;
  const textColor = isBokeh ? 'fill-brand' : 'focus';
  const highlight = attrs.highlight ?? { from: undefined, to: undefined };
  return (
    <Box as={NodeViewWrapper} display="inline">
      <Tooltip label={<UserHoverCard label={tooltipText} avatarUri={avatarUri} />} placement="top">
        <Text
          variant="body-xl"
          display="inline"
          cursor="pointer"
          color={textColor}
          fontSize="sm"
          fontWeight="bold"
          _hover={{ color: 'blue.600' }} //need to change based on new design feedback later
        >
          <HighlightedText text={attrs.label} from={highlight.from} to={highlight.to} />
        </Text>
      </Tooltip>
    </Box>
  );
};

export default MentionNode;
